'use client';

import Link from 'next/link';
import { ArrowRight, Wrench, BookOpen, ExternalLink } from 'lucide-react';

/**
 * Intent Steering Banner
 *
 * Helps reduce pogo-sticking by steering users to the right content based on their likely intent.
 * Shows prominently at the top of content when there's a likely intent mismatch.
 *
 * Use cases:
 * - Blog post about a concept → Tool that implements it (e.g., "What is Unix Timestamp" → Timestamp Converter)
 * - Tool page → Educational content (e.g., Timestamp Converter → "Learn about Unix timestamps")
 * - Tool page → Alternative tool with different features (e.g., "Looking for FILETIME converter?")
 */

interface Tool {
  name: string;
  slug: string;
  description?: string;
}

interface BlogPost {
  title: string;
  slug: string;
  excerpt?: string;
}

interface IntentSteeringBannerProps {
  // For blog posts linking to tools
  variant: 'blog-to-tool' | 'tool-to-blog' | 'tool-to-tool' | 'custom';

  // Primary action
  primaryTool?: Tool;
  primaryBlogPost?: BlogPost;

  // Custom content (for 'custom' variant)
  customTitle?: string;
  customDescription?: string;
  customLink?: string;
  customLinkText?: string;

  // Alternative suggestions (for handling multiple search intents)
  alternatives?: Array<{
    label: string;
    href: string;
    description?: string;
  }>;

  // Styling
  className?: string;
}

export default function IntentSteeringBanner({
  variant,
  primaryTool,
  primaryBlogPost,
  customTitle,
  customDescription,
  customLink,
  customLinkText,
  alternatives,
  className = '',
}: IntentSteeringBannerProps) {
  // Generate content based on variant
  const getContent = () => {
    switch (variant) {
      case 'blog-to-tool':
        if (!primaryTool) return null;
        return {
          icon: <Wrench className="w-5 h-5" />,
          title: 'Looking for a tool?',
          description: `Try our free ${primaryTool.name}`,
          linkText: `Use ${primaryTool.name}`,
          href: `/tools/${primaryTool.slug}`,
          bgColor: 'bg-gradient-to-r from-blue-50 to-indigo-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-900',
          linkColor: 'text-blue-700 hover:text-blue-900',
          iconBg: 'bg-blue-100',
        };

      case 'tool-to-blog':
        if (!primaryBlogPost) return null;
        return {
          icon: <BookOpen className="w-5 h-5" />,
          title: 'Want to learn more?',
          description: primaryBlogPost.excerpt || `Read our guide: ${primaryBlogPost.title}`,
          linkText: 'Read the guide',
          href: `/blog/${primaryBlogPost.slug}`,
          bgColor: 'bg-gradient-to-r from-emerald-50 to-teal-50',
          borderColor: 'border-emerald-200',
          textColor: 'text-emerald-900',
          linkColor: 'text-emerald-700 hover:text-emerald-900',
          iconBg: 'bg-emerald-100',
        };

      case 'tool-to-tool':
        if (!primaryTool) return null;
        return {
          icon: <ExternalLink className="w-5 h-5" />,
          title: 'Looking for something else?',
          description: `You might want our ${primaryTool.name}`,
          linkText: `Try ${primaryTool.name}`,
          href: `/tools/${primaryTool.slug}`,
          bgColor: 'bg-gradient-to-r from-amber-50 to-orange-50',
          borderColor: 'border-amber-200',
          textColor: 'text-amber-900',
          linkColor: 'text-amber-700 hover:text-amber-900',
          iconBg: 'bg-amber-100',
        };

      case 'custom':
        if (!customTitle || !customLink) return null;
        return {
          icon: <ArrowRight className="w-5 h-5" />,
          title: customTitle,
          description: customDescription || '',
          linkText: customLinkText || 'Learn more',
          href: customLink,
          bgColor: 'bg-gradient-to-r from-purple-50 to-pink-50',
          borderColor: 'border-purple-200',
          textColor: 'text-purple-900',
          linkColor: 'text-purple-700 hover:text-purple-900',
          iconBg: 'bg-purple-100',
        };

      default:
        return null;
    }
  };

  const content = getContent();
  if (!content) return null;

  return (
    <div
      className={`${content.bgColor} ${content.borderColor} border rounded-lg p-4 mb-6 ${className}`}
      role="complementary"
      aria-label="Related content suggestion"
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`${content.iconBg} p-2 rounded-lg flex-shrink-0 ${content.textColor}`}>
          {content.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`font-semibold ${content.textColor}`}>
            {content.title}
          </p>
          <p className={`text-sm ${content.textColor} opacity-80 mt-0.5`}>
            {content.description}
          </p>

          {/* Primary Link */}
          <Link
            href={content.href}
            className={`inline-flex items-center gap-1 mt-2 font-medium ${content.linkColor} transition-colors`}
          >
            {content.linkText}
            <ArrowRight className="w-4 h-4" />
          </Link>

          {/* Alternative Links */}
          {alternatives && alternatives.length > 0 && (
            <div className="mt-3 pt-3 border-t border-current/10">
              <p className={`text-xs ${content.textColor} opacity-60 mb-2`}>Or try:</p>
              <div className="flex flex-wrap gap-2">
                {alternatives.map((alt, idx) => (
                  <Link
                    key={idx}
                    href={alt.href}
                    className={`text-sm ${content.linkColor} hover:underline`}
                    title={alt.description}
                  >
                    {alt.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
